import { Alert, Button, Input, ModalV2 } from '@components'
import { useBusinessContext } from '@context'
import { IBusiness, updateBusiness } from '@query'
import { formatDate, formatNumber } from '@utils'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaLock } from 'react-icons/fa'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled, { useTheme } from 'styled-components'
import { LucaUsageDerived } from './lucaUsageVisuals'

interface Props {
  isVisible: boolean
  onClose: () => void
  view: LucaUsageDerived
  periodEnd: string
}

export const LucaCreditLimitModal: React.FC<Props> = ({ isVisible, onClose, view, periodEnd }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const queryClient = useQueryClient()
  const { slug, data: business } = useBusinessContext()
  const existingCap = business?.credit_cap ?? null
  const [capValue, setCapValue] = useState(existingCap?.toString() ?? '')
  const [fieldError, setFieldError] = useState<string | undefined>()

  useEffect(() => {
    if (!isVisible) return
    setCapValue(existingCap?.toString() ?? '')
    setFieldError(undefined)
  }, [existingCap, isVisible])

  const parsedCap = useMemo(() => {
    if (capValue.trim() === '') return null
    return Number.parseInt(capValue, 10)
  }, [capValue])

  const belowUsed = parsedCap != null && Number.isFinite(parsedCap) && parsedCap < view.usedCredits

  const resetDate = formatDate(dayjs(periodEnd).add(1, 'day'))

  const updateMutation = useMutation<IBusiness, unknown, { credit_cap: number | null }>({
    mutationFn: payload => updateBusiness({ slug }, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [slug] })
      await queryClient.invalidateQueries({ queryKey: [slug, 'luca-usage'] })
      toast.success(t('settings.billing.lucaUsage.limitModal.success'))
      onClose()
    }
  })

  const validate = (): number | null | undefined => {
    if (capValue.trim() === '') {
      setFieldError(undefined)
      return null
    }

    const value = Number.parseInt(capValue, 10)
    if (!Number.isFinite(value) || value < 1) {
      setFieldError(t('settings.billing.lucaUsage.limitModal.validationMin'))
      return undefined
    }

    setFieldError(undefined)
    return value
  }

  const handleSave = () => {
    const value = validate()
    if (value === undefined) return
    updateMutation.mutate({ credit_cap: value })
  }

  return (
    <ModalV2
      isVisible={isVisible}
      handleOnClose={onClose}
      header={t('settings.billing.lucaUsage.limitModal.title')}
      headerIcon={<FaLock color={theme.colors.primary} />}
      width={480}
      autoHeight
      footer={
        <FooterActions>
          <Button isSecondary onClick={onClose} disabled={updateMutation.isPending}>
            {t('general.cancel')}
          </Button>
          <Button
            intent="primary"
            onClick={handleSave}
            disabled={updateMutation.isPending}
            showSpinner={updateMutation.isPending}
          >
            {t('settings.billing.lucaUsage.limitModal.save')}
          </Button>
        </FooterActions>
      }
    >
      <ModalBody>
        <Description>{t('settings.billing.lucaUsage.limitModal.description')}</Description>

        <Input
          type="number"
          min={1}
          label={t('settings.billing.lucaUsage.limitModal.fieldLabel')}
          info={t('settings.billing.lucaUsage.limitModal.fieldHint')}
          name="credit_cap"
          value={capValue}
          onChange={event => {
            setCapValue(event.target.value)
            setFieldError(undefined)
          }}
          errors={fieldError ? { message: fieldError } : undefined}
          style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}
        />

        {belowUsed && (
          <Alert
            type="warning"
            title={t('settings.billing.lucaUsage.limitModal.belowUsedTitle')}
            description={t('settings.billing.lucaUsage.limitModal.belowUsedDescription', {
              used: formatNumber(view.usedCredits),
              date: resetDate
            })}
          />
        )}
      </ModalBody>
    </ModalV2>
  )
}

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const Description = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.luca.textMuted};
`

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  width: 100%;
`
